// src/pages/CaseHearingsPage.jsx
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

function formatDT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getHearingDocketText(h) {
  // Prefer the most recent entry that updated the hearing
  return (
    h?.last_updated_from_entry?.docket_text ||
    h?.created_from_entry?.docket_text ||
    h?.caption ||
    "Hearing"
  );
}

function getHearingDocuments(h) {
  return (
    h?.last_updated_from_entry?.documents ||
    h?.created_from_entry?.documents ||
    []
  );
}

function getDocUrl(doc) {
  // Prefer signed/presigned file_url, then file, then url
  return doc?.file_url || doc?.file || doc?.url || "";
}

function getDocLabel(doc, i) {
  return doc?.description || `Document ${i + 1}`;
}

function truncate(s, n = 2180) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n).trimEnd() + "…" : s;
}
function isoToLocalDateKey(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const API_URL = process.env.REACT_APP_API_URL;

function normalizeText(s) {
  return (s || "").toString().toLowerCase();
}

function parseTerms(input) {
  // Split by comma, trim, remove empties
  return (input || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

function StatusPill({ status }) {
  const base = "inline-flex items-center rounded-full px-2 py-0.5 text-xs border";
  const map = {
    scheduled: "bg-emerald-50 text-emerald-800 border-emerald-200",
    continued: "bg-amber-50 text-amber-800 border-amber-200",
    reset: "bg-blue-50 text-blue-800 border-blue-200",
    held: "bg-gray-50 text-gray-800 border-gray-200",
    canceled: "bg-rose-50 text-rose-800 border-rose-200",
    unknown: "bg-gray-50 text-gray-800 border-gray-200",
  };
  return <span className={`${base} ${map[status] || map.unknown}`}>{status}</span>;
}

export default function CaseHearingsPage() {
  const { caseId } = useParams();
  const [hearings, setHearings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filterMode, setFilterMode] = useState("exclude"); // 'exclude' | 'include'
  const [filterText, setFilterText] = useState("Chap 13 Conf");
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState(""); // YYYY-MM-DD

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);

        // If you're using JWT, keep it in localStorage. Support a few common keys.
        const token =
          localStorage.getItem("access") ||
          localStorage.getItem("accessToken") ||
          localStorage.getItem("token") ||
          "";

        const headers = { "Content-Type": "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const res = await fetch(`${API_URL}/case/${caseId}/hearings/`, {
          method: "GET",
          headers,
          credentials: "include",
        });

        if (!res.ok) {
          let detail = "";
          try {
            const data = await res.json();
            detail = data?.detail || data?.message || "";
          } catch (err) {
            // ignore json parse errors
          }
          throw new Error(detail || `Failed to load hearings (HTTP ${res.status})`);
        }

        const data = await res.json();
        if (!mounted) return;
        setHearings(Array.isArray(data) ? data : []);
        setErr("");
      } catch (e) {
        if (!mounted) return;
        setErr(e?.message || "Failed to load hearings");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, [caseId]);

  const terms = parseTerms(filterText).map(normalizeText);

  const filteredHearings = hearings.filter((h) => {
    const dateKey = isoToLocalDateKey(h?.starts_at);

    // Date range filtering (inclusive)
    if (startDate && dateKey && dateKey < startDate) return false;
    if (endDate && dateKey && dateKey > endDate) return false;

    // Text filtering
    if (terms.length === 0) return true;
    const hay = normalizeText(getHearingDocketText(h));
    const matchesAny = terms.some((t) => hay.includes(t));
    return filterMode === "include" ? matchesAny : !matchesAny;
  });

  if (loading) return <div className="text-gray-700">Loading hearings…</div>;
  if (err) return <div className="text-red-600">{err}</div>;

  return (
    <div className="space-y-4 text-gray-900">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Hearings</h1>
          <p className="text-sm text-gray-600">Court-set events parsed from docket entries.</p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-700">Filter mode</label>
            <select
              className="mt-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
            >
              <option value="exclude">Exclude matching</option>
              <option value="include">Include only matching</option>
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-700">Filter terms (comma-separated)</label>
            <input
              type="text"
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-[360px]"
              placeholder='e.g. "relief from stay, objection, confirmation"'
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-700">Start date</label>
            <input
              type="date"
              className="mt-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-700">End date</label>
            <input
              type="date"
              className="mt-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <button
            type="button"
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50"
            onClick={() => {
              setFilterText("");
              setStartDate("");
              setEndDate("");
            }}
            disabled={!filterText && !startDate && !endDate}
            title="Clear filter"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Date/Time</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Docket text</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Location</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredHearings.map((h) => (
              <tr key={h.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                  {formatDT(h.starts_at)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  <div className="text-gray-900">{truncate(getHearingDocketText(h))}</div>

                  {getHearingDocuments(h)?.length ? (
                    <div className="mt-2 flex flex-col gap-1">
                      {getHearingDocuments(h)
                        .map((doc, i) => {
                          const href = getDocUrl(doc);
                          if (!href) return null;
                          return (
                            <a
                              key={doc?.id || i}
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-blue-700 hover:underline"
                            >
                              {getDocLabel(doc, i)}
                            </a>
                          );
                        })
                        .filter(Boolean)}
                    </div>
                  ) : null}

                  {h.supersedes_id ? (
                    <div className="mt-1 text-xs text-gray-500">(rescheduled)</div>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{h.hearing_type}</td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {[h.location_name, h.location_city, h.location_state].filter(Boolean).join(", ")}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  <StatusPill status={h.status} />
                </td>
              </tr>
            ))}
            {filteredHearings.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-sm text-gray-500" colSpan={5}>
                  No hearings found for this case yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}